import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { date, dateTime, inr, mobile as fmtMobile, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { Avatar, Badge, Button, Card, CardHead, Chips, Modal, Notice, SearchInput, useToast } from '../components/ui';

/** How often the list and the figures pick up somebody who joined in the app. */
const REFRESH_MS = 15_000;

interface CommunityRow {
  key: string;
  label: string;
  capacity: number;
  joined: number;
  active: number;
  inactive: number;
  availableSlots: number;
  filledPercent: number;
  collectionPaise: number;
  feePaise: number;
  isFull: boolean;
}

interface Overview {
  communities: CommunityRow[];
  totals: { capacity: number; members: number; availableSlots: number; collectionPaise: number };
  next: { communityKey: string; memberNumber: number; memberId: string } | null;
  kpis: {
    totalMembers: number;
    capacity: number;
    availableSlots: number;
    joinedToday: number;
    qrCodesIssued: number;
    collectionPaise: number;
  };
}

interface MemberRow {
  id: string;
  memberId: string;
  memberCode: string;
  community: string;
  communityLabel: string;
  memberNumber: number;
  globalNumber: number;
  status: 'ACTIVE' | 'INACTIVE';
  qrToken: string;
  feePaise: number;
  joinedAt: string;
  name: string | null;
  mobile: string;
  email: string | null;
  profileImage: string | null;
  kycStatus: string;
  address: string | null;
  service: string | null;
  location: { id: string; name: string } | null;
  referredBy: { memberId: string; memberCode: string } | null;
  payment: {
    status: string;
    amountPaise: number;
    paidAt: string;
    transactionId: string;
    method: string;
  };
  slots: { total: number; used: number; available: number };
}

type StatusFilter = '' | 'ACTIVE' | 'INACTIVE';

/** Digits only, with the country code, as wa.me wants it. */
const whatsAppLink = (mobile: string) => `https://wa.me/${mobile.replace(/\D/g, '')}`;

/**
 * Membership communities (§24–28).
 *
 * Read and manage only. A membership is created in the app by the person
 * joining or by a member enrolling them, and nowhere else — so there is no
 * "add member" here, and the server has no route for one either.
 */
export function CommunitiesPage() {
  const { get, set } = useListParams();
  const search = get('search');
  const community = get('community');
  const status = (get('status') || '') as StatusFilter;
  const [viewing, setViewing] = useState<MemberRow | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const overview = useQuery({
    queryKey: ['communities-overview'],
    queryFn: () => api.get<Overview>('/admin/communities'),
    // Members arrive from the app, so the figures go stale on their own.
    refetchInterval: REFRESH_MS,
  });

  const setStatus = useMutation({
    mutationFn: (args: { id: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      api.patch(`/admin/communities/members/${args.id}/status`, { status: args.status }),
    onSuccess: (_d, args) => {
      toast(args.status === 'ACTIVE' ? 'Membership activated' : 'Membership deactivated');
      qc.invalidateQueries({ queryKey: ['community-members'] });
      qc.invalidateQueries({ queryKey: ['communities-overview'] });
      setViewing(null);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<MemberRow>> = [
    {
      key: 'member',
      header: 'Member',
      render: (r) => (
        <div className="row" style={{ gap: 10 }}>
          <Avatar name={r.name} src={r.profileImage} />
          <div>
            <div className="cell-strong">{r.name ?? 'Unnamed'}</div>
            <div className="cell-muted">{fmtMobile(r.mobile)}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'ids',
      header: 'Membership id',
      render: (r) => (
        <div>
          <div className="cell-strong" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {r.memberId}
          </div>
          {r.memberCode !== r.memberId && <div className="cell-muted">code: {r.memberCode}</div>}
        </div>
      ),
    },
    {
      key: 'community',
      header: 'Type',
      render: (r) => (
        <Badge tone={r.community === 'IUC' ? 'blue' : r.community === 'SUC' ? 'amber' : 'neutral'}>
          {r.community}
        </Badge>
      ),
    },
    {
      key: 'where',
      header: 'Location',
      render: (r) =>
        r.location?.name ?? r.address ?? <span className="cell-muted">—</span>,
    },
    {
      key: 'service',
      header: 'Service',
      render: (r) => r.service ?? <span className="cell-muted">—</span>,
    },
    {
      key: 'referral',
      header: 'Partner',
      render: (r) =>
        r.referredBy ? (
          <span className="cell-mono">{r.referredBy.memberCode}</span>
        ) : (
          <span className="cell-muted">—</span>
        ),
    },
    { key: 'joined', header: 'Registered', render: (r) => <span className="cell-muted">{date(r.joinedAt)}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (r) => <Badge tone={r.status === 'ACTIVE' ? 'green' : 'red'}>{r.status}</Badge>,
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="row" style={{ gap: 6 }}>
          <a
            href={whatsAppLink(r.mobile)}
            target="_blank"
            rel="noreferrer"
            className="btn btn--ghost"
            onClick={(e) => e.stopPropagation()}
          >
            WhatsApp
          </a>
          <Button variant="ghost" onClick={() => setViewing(r)}>
            View
          </Button>
        </div>
      ),
    },
  ];

  const o = overview.data;

  return (
    <div className="stack">
      {o && (
        <div className="grid grid--4" style={{ gap: 'var(--s5)' }}>
          <_Kpi label="Total members" value={num(o.kpis.totalMembers)} sub={`of ${num(o.kpis.capacity)} capacity`} />
          <_Kpi label="Available slots" value={num(o.kpis.availableSlots)} sub="across all communities" />
          <_Kpi label="Joined today" value={num(o.kpis.joinedToday)} sub="since midnight" />
          <_Kpi label="QR codes issued" value={num(o.kpis.qrCodesIssued)} sub="one per membership" />
        </div>
      )}

      <Card>
        <CardHead
          title="Membership communities"
          sub="Members register in the app. The server assigns the next number from a single global sequence — IUC first, then SUC, then TUC."
        />
        <div className="grid grid--3" style={{ padding: 'var(--s5)', gap: 'var(--s5)' }}>
          {(o?.communities ?? []).map((c) => (
            <div key={c.key} className="panel" style={{ padding: 'var(--s5)' }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <strong style={{ fontSize: 16 }}>{c.key}</strong>
                {c.isFull ? <Badge tone="red">Full</Badge> : <Badge tone="green">Open</Badge>}
              </div>
              <div className="cell-muted" style={{ marginBottom: 10 }}>
                {c.label}
              </div>

              <div
                style={{ height: 8, background: 'var(--bg-sunken)', borderRadius: 4, overflow: 'hidden', margin: '10px 0' }}
              >
                <div
                  style={{
                    width: `${Math.min(100, c.filledPercent)}%`,
                    height: '100%',
                    background: c.isFull ? 'var(--red)' : 'var(--blue)',
                  }}
                />
              </div>

              <div className="cell-strong" style={{ marginBottom: 8 }}>
                {num(c.joined)} / {num(c.capacity)}
              </div>

              <dl className="kv">
                <dt>Available</dt>
                <dd>{num(c.availableSlots)}</dd>
                <dt>Filled</dt>
                <dd>{c.filledPercent}%</dd>
                <dt>Collection</dt>
                <dd>{inr(c.collectionPaise)}</dd>
                {c.inactive > 0 && (
                  <>
                    <dt>Deactivated</dt>
                    <dd>{num(c.inactive)}</dd>
                  </>
                )}
              </dl>
            </div>
          ))}
        </div>

        {o && (
          <div
            className="row row--wrap"
            style={{ padding: '12px var(--s5)', borderTop: '1px solid var(--border)', gap: 'var(--s5)' }}
          >
            <span className="row" style={{ gap: 7 }}>
              <span className="cell-muted">Total collection</span>
              <strong>{inr(o.totals.collectionPaise)}</strong>
            </span>
            <span className="cell-muted">
              {o.next
                ? `The next member to register becomes ${o.next.memberId}`
                : 'Every community is full — no further memberships can be registered'}
            </span>
          </div>
        )}
      </Card>

      {o?.next === null && (
        <Notice tone="warn">
          Every community is at capacity, so registration in the app is refused. Raising a cap is a commercial
          decision rather than something the system should do on its own.
        </Notice>
      )}

      <DataTable<MemberRow>
        queryKey={['community-members', search, community, status]}
        path="/admin/communities/members"
        params={{ search, community, status }}
        columns={columns}
        onRowClick={(r) => setViewing(r)}
        // Somebody registering in the app should appear here without the page
        // being reloaded.
        refetchInterval={REFRESH_MS}
        emptyIcon="◎"
        emptyTitle="No members yet"
        emptyBody="A member appears here once their ₹500 has been captured in the app. Memberships cannot be created from this console."
        toolbar={
          <div className="row row--wrap" style={{ gap: 'var(--s4)' }}>
            <SearchInput
              value={search}
              onChange={(v) => set('search', v)}
              placeholder="Membership id, code, name or mobile"
            />
            <Chips
              value={community}
              options={[
                { value: '', label: 'All communities' },
                ...(o?.communities ?? []).map((c) => ({ value: c.key, label: c.key, count: c.joined })),
              ]}
              onChange={(v) => set('community', v)}
            />
            <Chips<StatusFilter>
              value={status}
              options={[
                { value: '', label: 'Any status' },
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Deactivated' },
              ]}
              onChange={(v) => set('status', v)}
            />
          </div>
        }
      />

      {viewing && (
        <Modal onClose={() => setViewing(null)} title={viewing.memberId} wide>
          <div className="stack">
            <div className="row" style={{ gap: 'var(--s5)', alignItems: 'flex-start' }}>
              <div className="stack" style={{ gap: 10, alignItems: 'center' }}>
                <Avatar name={viewing.name} src={viewing.profileImage} size="lg" />
                {/* Served by the API so this console and the member's own app
                    show one code rather than two renderings of the same data. */}
                <img
                  src={`/api/v1/communities/verify/${viewing.qrToken}/qr.svg`}
                  alt={`QR code for ${viewing.memberId}`}
                  width={150}
                  height={150}
                  style={{ border: '1px solid var(--border)', borderRadius: 8, background: '#fff' }}
                />
                <a
                  href={`/api/v1/communities/verify/${viewing.qrToken}/qr.svg`}
                  download={`${viewing.memberId}.svg`}
                  className="btn btn--ghost"
                >
                  Download QR
                </a>
              </div>

              <dl className="kv" style={{ flex: 1 }}>
                <dt>Name</dt>
                <dd>{viewing.name ?? 'Unnamed'}</dd>
                <dt>Membership id</dt>
                <dd>{viewing.memberId}</dd>
                <dt>Member code</dt>
                <dd>{viewing.memberCode}</dd>
                <dt>Type</dt>
                <dd>
                  {viewing.community} · {viewing.communityLabel}
                </dd>
                <dt>Mobile</dt>
                <dd>
                  {fmtMobile(viewing.mobile)}{' '}
                  <a href={whatsAppLink(viewing.mobile)} target="_blank" rel="noreferrer">
                    WhatsApp →
                  </a>
                </dd>
                <dt>Email</dt>
                <dd>{viewing.email ?? '—'}</dd>
                <dt>Address</dt>
                <dd>{viewing.address ?? '—'}</dd>
                <dt>Location</dt>
                <dd>{viewing.location?.name ?? '—'}</dd>
                <dt>Service</dt>
                <dd>{viewing.service ?? '—'}</dd>
                <dt>Partner</dt>
                <dd>{viewing.referredBy?.memberCode ?? '—'}</dd>
                <dt>KYC</dt>
                <dd>{viewing.kycStatus}</dd>
                <dt>Registered</dt>
                <dd>{dateTime(viewing.joinedAt)}</dd>
                <dt>Payment</dt>
                <dd>
                  {inr(viewing.payment.amountPaise)} · {viewing.payment.status} · {date(viewing.payment.paidAt)}
                  {viewing.payment.method ? ` · ${viewing.payment.method}` : null}
                </dd>
                <dt>Transaction id</dt>
                <dd className="cell-mono" style={{ wordBreak: 'break-all' }}>
                  {viewing.payment.transactionId}
                </dd>
                <dt>Buy &amp; Sell slots</dt>
                <dd>
                  {viewing.slots.total === 0 ? (
                    'None'
                  ) : (
                    <>
                      {viewing.slots.total} total · {viewing.slots.used} used ·{' '}
                      {viewing.slots.available} available
                    </>
                  )}
                </dd>
                <dt>Status</dt>
                <dd>
                  <Badge tone={viewing.status === 'ACTIVE' ? 'green' : 'red'}>{viewing.status}</Badge>
                </dd>
              </dl>
            </div>

            <Notice tone="info">
              Deactivating keeps the membership number. It is printed on their card, and issuing it again would make
              two cards agree.
            </Notice>

            <div className="row" style={{ justifyContent: 'flex-end', gap: 'var(--s4)' }}>
              {viewing.status === 'ACTIVE' ? (
                <Button
                  variant="danger"
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ id: viewing.id, status: 'INACTIVE' })}
                >
                  Deactivate
                </Button>
              ) : (
                <Button
                  disabled={setStatus.isPending}
                  onClick={() => setStatus.mutate({ id: viewing.id, status: 'ACTIVE' })}
                >
                  Activate
                </Button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function _Kpi({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <Card>
      <div style={{ padding: 'var(--s5)' }}>
        <div className="cell-muted">{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2 }}>{value}</div>
        <div className="cell-muted">{sub}</div>
      </div>
    </Card>
  );
}
